import React from "react";

/**
 * Step 1: Welcome & Platform Selection
 * User selects which platform they're migrating from (Square, Squire, Booksy, Other)
 */
function Step1Welcome({ migrationData, updateMigrationData, nextStep }) {
  // Determine if this is a freelancer migration (freelancers don't have staff)
  const isFreelancer = migrationData?.type === "freelancer";

  const platforms = [
    {
      id: "square",
      name: "Square",
      logo: "⬜",
      description: "Point of Sale & Appointments",
      features: isFreelancer
        ? ["Services", "Customers"]
        : ["Services", "Customers", "Staff"],
      exportInstructions: "Dashboard → Reports → Export Data",
    },
    {
      id: "squire",
      name: "Squire",
      logo: "💈",
      description: "Barbershop Management",
      features: ["Services", "Customers", "Walk-ins", "Products"],
      exportInstructions: "Settings → Data Export → Download CSV",
    },
    {
      id: "booksy",
      name: "Booksy",
      logo: "📅",
      description: "Booking & Scheduling",
      features: isFreelancer
        ? ["Services", "Customers"]
        : ["Services", "Customers", "Staff"],
      exportInstructions: "Settings → Export → Download All Data",
    },
    {
      id: "other",
      name: "Other Platform",
      logo: "📊",
      description: "Generic CSV Import",
      features: ["Custom CSV Format"],
      exportInstructions: "Export your data as CSV files",
    },
  ];

  const selectPlatform = (platformId) => {
    updateMigrationData({ sourcePlatform: platformId });
  };

  const handleContinue = () => {
    if (migrationData.sourcePlatform) {
      nextStep();
    } else {
      alert("Please select a platform to continue");
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.welcome}>
        <h2 style={styles.heading}>Welcome to Ogestyle Migration Wizard</h2>
        <p style={styles.subheading}>
          We'll help you migrate your business data in just a few simple steps.
          The entire process typically takes less than 5 minutes.
        </p>
      </div>

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>
          Which platform are you migrating from?
        </h3>
        <p style={styles.sectionDescription}>
          Select your current booking system to get customized import
          instructions
        </p>

        <div style={styles.platformGrid}>
          {platforms.map((platform) => (
            <div
              key={platform.id}
              style={{
                ...styles.platformCard,
                ...(migrationData.sourcePlatform === platform.id
                  ? styles.platformCardSelected
                  : {}),
              }}
              onClick={() => selectPlatform(platform.id)}
            >
              <div style={styles.platformLogo}>{platform.logo}</div>
              <h4 style={styles.platformName}>{platform.name}</h4>
              <p style={styles.platformDescription}>{platform.description}</p>

              <div style={styles.platformFeatures}>
                <strong>We'll import:</strong>
                <ul style={styles.featureList}>
                  {platform.features.map((feature, idx) => (
                    <li key={idx}>✓ {feature}</li>
                  ))}
                </ul>
              </div>

              {migrationData.sourcePlatform === platform.id && (
                <div style={styles.selectedBadge}>Selected ✓</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {migrationData.sourcePlatform && (
        <div style={styles.instructions}>
          <h3 style={styles.instructionsTitle}>
            📋 How to Export from{" "}
            {platforms.find((p) => p.id === migrationData.sourcePlatform)?.name}
          </h3>
          <p style={styles.instructionsText}>
            {
              platforms.find((p) => p.id === migrationData.sourcePlatform)
                ?.exportInstructions
            }
          </p>
          <div style={styles.instructionsNote}>
            <strong>Tip:</strong> Have your CSV files ready before clicking
            Continue. You'll be able to upload multiple files in the next step.
          </div>
        </div>
      )}

      <div style={styles.benefits}>
        <h3 style={styles.benefitsTitle}>Why Migrate to Ogestyle?</h3>
        <div style={styles.benefitsGrid}>
          <div style={styles.benefitCard}>
            <div style={styles.benefitIcon}>💰</div>
            <h4 style={styles.benefitTitle}>Lower Fees</h4>
            <p style={styles.benefitText}>
              Keep more of your revenue with our competitive transaction fees
            </p>
          </div>
          <div style={styles.benefitCard}>
            <div style={styles.benefitIcon}>🛍️</div>
            <h4 style={styles.benefitTitle}>Sell Products</h4>
            <p style={styles.benefitText}>
              Built-in e-commerce for retail products - not available on most
              platforms
            </p>
          </div>
          <div style={styles.benefitCard}>
            <div style={styles.benefitIcon}>📦</div>
            <h4 style={styles.benefitTitle}>Service Packages</h4>
            <p style={styles.benefitText}>
              Bundle services and sell them at a discount to increase revenue
            </p>
          </div>
          <div style={styles.benefitCard}>
            <div style={styles.benefitIcon}>🎯</div>
            <h4 style={styles.benefitTitle}>Better Features</h4>
            <p style={styles.benefitText}>
              {isFreelancer
                ? "Advanced booking, scheduling, and analytics all in one place"
                : "Advanced booking, staff management, and analytics all in one place"}
            </p>
          </div>
        </div>
      </div>

      <div style={styles.actions}>
        <button
          onClick={handleContinue}
          disabled={!migrationData.sourcePlatform}
          style={{
            ...styles.continueButton,
            ...(migrationData.sourcePlatform
              ? {}
              : styles.continueButtonDisabled),
          }}
        >
          Continue to Upload →
        </button>
      </div>
    </div>
  );
}

const styles = {
  container: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "32px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
  welcome: {
    textAlign: "center",
    marginBottom: "48px",
  },
  heading: {
    fontSize: "32px",
    fontWeight: 700,
    color: "#333",
    marginBottom: "16px",
  },
  subheading: {
    fontSize: "18px",
    color: "#666",
    maxWidth: "600px",
    margin: "0 auto",
    lineHeight: 1.6,
  },
  section: {
    marginBottom: "48px",
  },
  sectionTitle: {
    fontSize: "24px",
    fontWeight: 600,
    color: "#333",
    marginBottom: "8px",
  },
  sectionDescription: {
    fontSize: "16px",
    color: "#666",
    marginBottom: "24px",
  },
  platformGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "20px",
    marginBottom: "32px",
  },
  platformCard: {
    border: "2px solid #e0e0e0",
    borderRadius: "12px",
    padding: "24px",
    cursor: "pointer",
    transition: "all 0.3s ease",
    position: "relative",
    backgroundColor: "#fff",
  },
  platformCardSelected: {
    border: "2px solid #2196F3",
    backgroundColor: "#f0f8ff",
    boxShadow: "0 4px 12px rgba(33, 150, 243, 0.2)",
  },
  platformLogo: {
    fontSize: "48px",
    marginBottom: "16px",
  },
  platformName: {
    fontSize: "20px",
    fontWeight: 600,
    color: "#333",
    marginBottom: "8px",
  },
  platformDescription: {
    fontSize: "14px",
    color: "#666",
    marginBottom: "16px",
  },
  platformFeatures: {
    fontSize: "14px",
    color: "#333",
  },
  featureList: {
    listStyle: "none",
    padding: 0,
    margin: "8px 0 0 0",
  },
  selectedBadge: {
    position: "absolute",
    top: "16px",
    right: "16px",
    backgroundColor: "#4CAF50",
    color: "#fff",
    padding: "4px 12px",
    borderRadius: "16px",
    fontSize: "12px",
    fontWeight: 600,
  },
  instructions: {
    backgroundColor: "#fff3cd",
    border: "1px solid #ffc107",
    borderRadius: "8px",
    padding: "20px",
    marginBottom: "32px",
  },
  instructionsTitle: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#333",
    marginBottom: "12px",
  },
  instructionsText: {
    fontSize: "16px",
    color: "#666",
    marginBottom: "12px",
  },
  instructionsNote: {
    fontSize: "14px",
    color: "#666",
    backgroundColor: "#fff",
    padding: "12px",
    borderRadius: "6px",
  },
  benefits: {
    marginBottom: "48px",
  },
  benefitsTitle: {
    fontSize: "24px",
    fontWeight: 600,
    color: "#333",
    marginBottom: "24px",
    textAlign: "center",
  },
  benefitsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "20px",
  },
  benefitCard: {
    textAlign: "center",
    padding: "20px",
  },
  benefitIcon: {
    fontSize: "48px",
    marginBottom: "12px",
  },
  benefitTitle: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#333",
    marginBottom: "8px",
  },
  benefitText: {
    fontSize: "14px",
    color: "#666",
    lineHeight: 1.5,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: "32px",
  },
  continueButton: {
    padding: "14px 32px",
    backgroundColor: "#2196F3",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "background-color 0.3s ease",
  },
  continueButtonDisabled: {
    backgroundColor: "#ccc",
    cursor: "not-allowed",
  },
};

export default Step1Welcome;
